import React, { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Search, Edit, Trash2 } from 'lucide-react';
import { customerService, CustomerData } from '../services/customerServiceSupabase';
import type { Customer } from '../types';
import { Button } from '@/components/ui/Button';
import { useDebounce } from '../hooks/useDebounce';
import { useOfflineStatus } from '../hooks/useOfflineStatus';
import OfflineLoadingState from '../components/offline/OfflineLoadingState';
import { CustomerForm } from '../components/customers/CustomerForm';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CustomerDetails } from '../components/customers/CustomerDetails';
import { useTenant } from '../contexts/TenantContext';

const Customers: React.FC = () => {
  const { tenantId } = useTenant();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const { isOnline } = useOfflineStatus();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [viewingCustomerId, setViewingCustomerId] = useState<string | null>(null);


  const fetchCustomers = useCallback(async (page: number, search: string) => {
    if (!tenantId) {
      setError('Δεν έχει επιλεγεί επιχείρηση. Συνδεθείτε ξανά.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await customerService.getCustomers(tenantId, page, pagination.limit, search);
      const pages = Math.max(1, response.pagination.pages || 1);
      setCustomers(response.data);
      setPagination({
        ...response.pagination,
        total: Math.max(0, response.pagination.total || 0),
        pages,
        page: Math.min(page, pages),
      });
    } catch (err) {
      setError('Αποτυχία φόρτωσης πελατών. Δοκιμάστε ξανά.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, tenantId]);

  useEffect(() => {
    fetchCustomers(pagination.page, debouncedSearchTerm);
  }, [pagination.page, debouncedSearchTerm, fetchCustomers]);
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPagination(p => ({ ...p, page: 1 })); // Reset to first page on search
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setPagination(p => ({ ...p, page: newPage }));
    }
  };

  const handleAddCustomer = () => {
    setSelectedCustomer(null);
    setIsModalOpen(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsModalOpen(true);
  };

  const handleDeleteCustomer = (customer: Customer) => {
    setCustomerToDelete(customer);
    setIsDeleteDialogOpen(true);
  };
  
  const confirmDelete = async () => {
    if (!customerToDelete) return;
    if (!tenantId) {
      setError('Δεν έχει επιλεγεί επιχείρηση. Συνδεθείτε ξανά.');
      return;
    }
    const deleteId = customerToDelete.id;
    // Optimistically drop from UI
    setCustomers((prev) => prev.filter((c) => c.id !== deleteId));
    setPagination((prev) => ({
      ...prev,
      total: Math.max(0, prev.total - 1),
    }));
    // Close dialog immediately for better UX
    setIsDeleteDialogOpen(false);
    setCustomerToDelete(null);

    try {
      await customerService.deleteCustomer(tenantId, deleteId);
    } catch (err) {
      setError((err as Error).message || 'Αποτυχία διαγραφής πελάτη.');
      // Refetch to restore the customer in the list since the delete failed
      fetchCustomers(pagination.page, debouncedSearchTerm);
    }
  };

  const handleSaveCustomer = async (data: CustomerData) => {
    if (!tenantId) {
      setError('Δεν έχει επιλεγεί επιχείρηση. Συνδεθείτε ξανά.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      if (selectedCustomer) {
        await customerService.updateCustomer(tenantId, selectedCustomer.id, data);
      } else {
        await customerService.createCustomer(tenantId, data);
      }
      setIsModalOpen(false);
      fetchCustomers(pagination.page, debouncedSearchTerm); // Refresh list
    } catch (err) {
      setError((err as Error).message || 'Αποτυχία αποθήκευσης πελάτη.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewDetails = (customerId: string) => {
    setViewingCustomerId(customerId);
  };

  const handleEditFromDetails = (customer: Customer) => {
    setViewingCustomerId(null); // Close details modal
    handleEditCustomer(customer); // Open edit modal
  };


  return (
    <div className="space-y-4 md:space-y-6">
       {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center space-x-3">
          <Users className="w-6 h-6 md:w-8 md:h-8 text-green-500 flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-xl md:text-3xl font-bold text-foreground truncate">Πελάτες</h1>
            <p className="text-sm md:text-base text-muted-foreground hidden md:block">Διαχείριση βάσης δεδομένων πελατών</p>
          </div>
        </div>
        <Button onClick={handleAddCustomer} className="touch-target self-start md:self-auto" size="sm">
          <Plus className="mr-2 h-4 w-4" />
          <span className="hidden sm:inline">Προσθήκη Πελάτη</span>
          <span className="sm:hidden">Προσθήκη</span>
        </Button>
      </div>

       {/* Main Content */}
      <div className="bg-card rounded-lg shadow-sm border border-border">
        <div className="p-3 md:p-4 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Αναζήτηση πελάτη (όνομα, τηλέφωνο)..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="pl-10 pr-4 py-2 w-full border border-border rounded-lg focus:ring-green-500 focus:border-green-500 touch-target bg-background text-foreground"
            />
          </div>
        </div>
        
        <OfflineLoadingState 
          isLoading={loading}
          isOnline={isOnline}
          error={error}
          onRetry={() => fetchCustomers(pagination.page, debouncedSearchTerm)}
          loadingMessage="Φόρτωση πελατών..."
          offlineMessage="Χρήση τοπικών δεδομένων πελατών"
        />

        {!loading && !error && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-accent">
                  <tr>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Όνομα</th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden sm:table-cell">Τηλέφωνο</th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden md:table-cell">Κρατήσεις</th>
                    <th className="px-3 md:px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Ενέργειες</th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {customers.map((customer) => (
                    <tr
                      key={customer.id}
                      className="hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
                      tabIndex={0}
                      onClick={() => handleViewDetails(customer.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleViewDetails(customer.id);
                        }
                      }}
                    >
                      <td className="px-3 md:px-6 py-4">
                        <div className="text-sm font-medium text-card-foreground hover:text-green-600 touch-target">
                          {customer.name}
                        </div>
                        {customer.notes && <div className="text-xs text-muted-foreground mt-1 truncate">{customer.notes}</div>}
                        {/* Show phone on mobile */}
                        <div className="text-xs text-muted-foreground mt-1 sm:hidden">
                          {customer.phone}
                        </div>
                        {/* Show reservation count on mobile */}
                        <div className="text-xs text-muted-foreground mt-1 md:hidden">
                          {customer.reservation_count || 0} κρατήσεις
                        </div>
                      </td>
                      <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-muted-foreground hidden sm:table-cell">{customer.phone}</td>
                      <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-muted-foreground hidden md:table-cell">{customer.reservation_count || 0}</td>
                      <td className="px-3 md:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-1 md:space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCustomer(customer);
                            }}
                            className="touch-target"
                          >
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Επεξεργασία</span>
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCustomer(customer);
                            }}
                            className="touch-target"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Διαγραφή</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
             {customers.length === 0 && !loading && (
              <div className="p-8 text-center">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-card-foreground mb-2">Δεν βρέθηκαν πελάτες</h3>
                <p className="text-muted-foreground">Δοκιμάστε να αλλάξετε την αναζήτησή σας ή προσθέστε έναν νέο πελάτη.</p>
              </div>
            )}
            <div className="p-3 md:p-4 border-t border-border">
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <span className="text-xs md:text-sm text-muted-foreground text-center sm:text-left">
                  Σελίδα {pagination.page} από {pagination.pages} (Σύνολο: {pagination.total} πελάτες)
                </span>
                <div className="flex justify-center space-x-2">
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="touch-target"
                  >
                    <span className="hidden sm:inline">Προηγούμενη</span>
                    <span className="sm:hidden">Προηγ.</span>
                  </Button>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages}
                    className="touch-target"
                  >
                    <span className="hidden sm:inline">Επόμενη</span>
                    <span className="sm:hidden">Επόμ.</span>
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedCustomer ? 'Επεξεργασία Πελάτη' : 'Προσθήκη Νέου Πελάτη'}</DialogTitle>
            <DialogDescription>
              {selectedCustomer ? 'Επεξεργαστείτε τα στοιχεία του πελάτη.' : 'Συμπληρώστε τα στοιχεία για να δημιουργήσετε έναν νέο πελάτη.'}
            </DialogDescription>
          </DialogHeader>
          <CustomerForm
            customer={selectedCustomer}
            onSave={handleSaveCustomer}
            onCancel={() => {
              setIsModalOpen(false);
              setError(null); // Clear error when closing modal
            }}
            isSaving={isSaving}
            error={error}
          />
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
       <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Είστε σίγουροι;</AlertDialogTitle>
            <AlertDialogDescription>
              Αυτή η ενέργεια δεν μπορεί να αναιρεθεί. Θα διαγραφεί οριστικά ο πελάτης
              <span className="font-bold"> {customerToDelete?.name}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Διαγραφή</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Details Modal */}
      <Dialog open={!!viewingCustomerId} onOpenChange={(isOpen) => !isOpen && setViewingCustomerId(null)}>
        <DialogContent className="w-[95vw] max-w-3xl md:px-8">
          <DialogHeader>
            <DialogTitle>Προφίλ Πελάτη</DialogTitle>
          </DialogHeader>
          {viewingCustomerId && (
            <CustomerDetails
              customerId={viewingCustomerId}
              onEdit={handleEditFromDetails}
              onClose={() => setViewingCustomerId(null)}
            />
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Customers; 